import { render, h } from 'preact';
import bem from 'easy-bem';
import {
  CheckoutProvider,
  StoreProvider,
} from '@primer-io/shared-library/contexts';
import { locateElement } from '../../utils/dom';
import { PaymentMethods } from '../../payment-methods/PaymentMethods';
import { CheckoutPaymentMethodConfig } from '../CheckoutPaymentMethodConfig';
import { CheckoutTokenizationHandlers } from '../CheckoutTokenizationHandlers';
import { BasePaymentMethod } from '../../payment-methods/BasePaymentMethod';
import {
  PrimerSinglePaymentMethodCheckout,
  SinglePaymentMethodCheckoutOptions,
  SupportedLocale,
} from '../../types';
import { setValue } from '../DomUtilities';
import { ElementID, Scene } from '../../enums/Checkout';
import { Translations } from '../../assets/translations/Translations';
import { IFrameEventType } from '../../core/IFrameEventType';
import { IFrameMessagePayload } from '../../core/IFrameMessage';
import { ClientContext } from '../../core/ClientContextFactory';

import { createSinglePaymentMethodCheckoutViewUtils } from '../ui/ViewUtils';

import CheckoutRoot from '../../scenes/CheckoutRoot';

import { createStore } from '../../store/CheckoutStoreFactory';

import { SceneTransition } from '../SceneTransition';
import { PrimerClientError, ErrorCode } from '../../errors';
import { getSystemLocale } from '../../utils/getSystemLocale';

const renderRoot = ({
  options,
  checkout,
  viewUtils,
  context,
  transitions,
  store,
  root,
}: {
  options;
  checkout;
  viewUtils;
  context?;
  transitions;
  store;
  root;
}) =>
  render(
    <CheckoutProvider
      value={{
        options,
        checkout,
        viewUtils,
        context,
        transitions,
        className: bem('PrimerCheckout'),
      }}
    >
      <StoreProvider value={store}>
        <CheckoutRoot />
      </StoreProvider>
    </CheckoutProvider>,
    root,
  );

export class SinglePaymentMethodCheckout {
  static async create(
    getContext: () => Promise<ClientContext>,
    options: SinglePaymentMethodCheckoutOptions,
  ): Promise<PrimerSinglePaymentMethodCheckout> {
    const parent = locateElement(options.container);

    if (parent == null) {
      return onConfigError(
        `Attempted to mount checkout UI at "${options.container}" but the element could not be found`,
      );
    }

    const root = document.createElement('div');
    parent.prepend(root);

    ///////////////////////////////////////////
    // Context
    ///////////////////////////////////////////
    const contextPromise = getContext();

    ///////////////////////////////////////////
    // Render
    ///////////////////////////////////////////
    const store = createStore();
    store.setLocale(options.locale ?? (getSystemLocale() as SupportedLocale));

    const viewUtils = createSinglePaymentMethodCheckoutViewUtils(
      store,
      options,
    );
    const transitions = new SceneTransition();

    async function validateCardForm() {
      return { valid: true, validationErrors: [] };
    }

    async function tokenizeCard() {
      return Promise.resolve();
    }

    const checkout = {
      validate: validateCardForm,
      tokenize: tokenizeCard,
    };

    renderRoot({ options, checkout, viewUtils, transitions, store, root });

    ///////////////////////////////////////////
    // State
    ///////////////////////////////////////////
    const translation = Translations.get(options.locale);

    store.produceState((draft) => {
      draft.translation = translation;
      draft.options.showSavePaymentMethod = false;
      // Remove check once purchaseInfo is removed
      if (options.purchaseInfo) {
        console.warn(
          'purchaseInfo has been deprecated, please use orderDetails instead',
        );
        const updatedOrderDetails = {
          ...options.orderDetails,
          totalAmount: options.purchaseInfo.totalAmount.value,
          currencyCode: options.purchaseInfo.totalAmount.currency,
        };
        draft.options.orderDetails = updatedOrderDetails ?? null;
      } else {
        draft.options.orderDetails = options.orderDetails ?? null;
      }
    });

    const context = await contextPromise;
    const config = {
      ...CheckoutPaymentMethodConfig.create(store, viewUtils, options),
      ...CheckoutTokenizationHandlers.create(context, viewUtils, options),
    };

    const paymentMethods: Record<
      string,
      BasePaymentMethod
    > = await PaymentMethods.create(
      context,
      config,
      store,
      viewUtils.styleManager,
    );

    const isPaymentMethodLoaded =
      Object.values(paymentMethods).findIndex(
        (paymentMethod) => paymentMethod.type === options.paymentMethod,
      ) !== -1;

    if (!isPaymentMethodLoaded) {
      throw PrimerClientError.fromErrorCode(
        ErrorCode.PAYMENT_METHOD_NOT_SETUP,
        {
          message: `Can't load ${options.paymentMethod}.`,
        },
      );
    }

    store.setPaymentMethods(paymentMethods);

    context.messageBus.on(
      IFrameEventType.CARDHOLDER_NAME,
      (e: IFrameMessagePayload<string>) => {
        setValue(ElementID.CARDHOLDER_NAME_INPUT, e.payload);
      },
    );

    renderRoot({
      options,
      checkout,
      viewUtils,
      context,
      transitions,
      store,
      root,
    });

    // TODO: enable different form
    store.setScene('gocardless/form');
    store.setIsLoading(false);

    return checkout;
  }
}

function onConfigError(message: string): PrimerSinglePaymentMethodCheckout {
  console.error(message);
  return {
    tokenize: () => Promise.resolve(),
    validate: () => Promise.resolve({ valid: false, validationErrors: [] }),
  };
}
